import * as React from 'react';
import { OverlayTrigger, Tooltip, Table } from 'react-bootstrap';
import './bloodwork-ref-range-analysis.css';
import { ReferenceRangeChart } from '../charts/ReferenceRangeChart';
import { EGFRReferenceRangeChart } from '../charts/EGFRReferenceRangeChart';
import {
  Bloodwork,
  Section,
  PanelMarker,
  MenstrualPhase,
  CustomTextMarker,
} from '../../models';
import { RDWReferenceRangeChart } from '../charts/RDWReferenceRangeChart';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  formatCollectionDate,
  formatCollectionDateShort,
} from '../../helpers/previousOnlyMarkers';
import { MyRangeDot, isMyRange, myRangeNote } from './my-range-dot';
import {
  hyphenateLabel,
  textLabelColumnWidth,
  LABEL_COLUMN_PX,
} from '../../helpers/labelHyphenation';

type Props = {
  title: string;
  section: Section;
  bloodwork: Bloodwork;
  previousBloodwork?: Bloodwork;
  panelMarkers: PanelMarker[];
  /**
   * This section's practitioner-typed markers whose result is WORDS.
   *
   * Printed as plain rows beneath the bar graphs, in the same table, so they
   * line up with them and travel with them into the PDF. Deliberately a
   * separate prop rather than more PanelMarkers: there is no value to plot and
   * no range to plot it against (see CustomTextMarker).
   */
  textMarkers?: CustomTextMarker[];
  // Optional: sections with no bibliography anchor (e.g. the practitioner's own
  // custom markers) omit it, and the "learn more" affordance is skipped.
  infoLink?: string;
  // PDF export rendering. The external-link ("learn more") icon next to each
  // marker name is interactive and only useful on screen — it never renders in
  // the exported PDF — so we omit it here, which also lets the marker name sit
  // flush-right with the value/unit beneath it.
  forPdf?: boolean;
};

type State = {
  showRefRangeModal: boolean;
};

export class ReferenceRangeAnalysis extends React.Component<Props, {}> {
  state: State = {
    showRefRangeModal: false,
  };

  showRefRangeModal = () => {
    const { infoLink } = this.props;
    if (infoLink) {
      window.open(infoLink);
    } else {
      this.setState({
        showRefRangeModal: true,
      });
    }
  };

  onHideRefRangeModal = () => {
    this.setState({
      showRefRangeModal: false,
    });
  };

  /**
   * Markers in this panel whose bands are the practitioner's own ranges
   * ("My Ranges"), in the order they are charted.
   *
   * Read off the range the lookup already returned, not off the profile: the
   * override was stamped on when it was applied, so this says whose numbers
   * drew THIS client's bar rather than which markers the practitioner has
   * ever edited.
   */
  private myRangeMarkers(panelMarkers: PanelMarker[]): PanelMarker[] {
    if (this.props.forPdf) {
      return [];
    }
    return (panelMarkers || []).filter(pm => isMyRange(pm));
  }

  /**
   * The chart label split at its last space, so the last word can be kept on
   * one line with the link icon that follows it. Head is everything before
   * the last word (with its trailing space), empty for a one-word name.
   */
  chartLabelHead(pm: PanelMarker): string {
    const label = pm.field.getChartLabel();
    const cut = label.lastIndexOf(' ');
    return cut < 0 ? '' : label.slice(0, cut + 1);
  }

  chartLabelTail(pm: PanelMarker): string {
    const label = pm.field.getChartLabel();
    const cut = label.lastIndexOf(' ');
    return cut < 0 ? label : label.slice(cut + 1);
  }

  labCorpInfoTooltip = (panelMarker: PanelMarker) => {
    // tslint:disable-next-line:max-line-length
    const labCorpInfoTooltip =
      'Click this icon to learn more about how the reference ranges for this marker were determined.';
    return <Tooltip id="modal-tooltip">{labCorpInfoTooltip}</Tooltip>;
  };

  displayChart(panelMarker: PanelMarker, index: number) {
    // The two collection dates ride along to every bar's hover box. One
    // funnel renders all three chart types, so they are formatted once here
    // rather than in each of them.
    const dates = {
      currentDate: formatCollectionDateShort(this.props.bloodwork?.collectionDate),
      previousDate: formatCollectionDateShort(this.props.previousBloodwork?.collectionDate),
    };
    let chart = (
      <ReferenceRangeChart
        panelMarker={panelMarker}
        chartContainerClass="reference-range-chart-container"
        showPlotBandLabels={index === 1}
        {...dates}
      />
    );
    if (panelMarker.isEGFR() || panelMarker.field.useEGFRChart) {
      chart = (
        <EGFRReferenceRangeChart
          panelMarker={panelMarker}
          chartContainerClass="reference-range-chart-container"
          {...dates}
        />
      );
    } else if (panelMarker.field.useRDWChart) {
      chart = (
        <RDWReferenceRangeChart
          panelMarker={panelMarker}
          chartContainerClass="reference-range-chart-container"
          {...dates}
        />
      );
    }
    return chart;
  }

  openWindow(pm?: PanelMarker) {
    if (pm) {
      window.open(pm.field.labCorpLink);
    } else if (this.props.infoLink) {
      window.open(this.props.infoLink);
    }
  }

  // `table-row`, never `block`: a block <tr> is not a row, so the browser wraps
  // it in an anonymous cell that lands in the table's FIRST column, and every
  // real cell of a text row then sizes against that column. That is how a
  // two-word result ended up pinned to the panel's right edge, its marker name
  // 500px right of every charted marker's name, while the bar graphs lost the
  // width the (squeezed) second column was holding. Rows share one column model
  // or the labels do not line up.
  showChart(index: number): string {
    return index === 0 ? 'none' : 'table-row';
  }

  // A "previous-only" marker is blank in the current test but carries a
  // previous-test value (see BloodworkAnalysis.getPreviousOnlyPanelMarkers).
  // Its chart shows just the previous (blue) blood drop, so the value/label
  // beneath it must come from the previous marker, not the (empty) current one.
  isPreviousOnly(pm: PanelMarker): boolean {
    return (
      !pm.hasValue() &&
      !!pm.previousPanelMarker &&
      pm.previousPanelMarker.hasValue()
    );
  }

  formatMenstrualPhase(phase: MenstrualPhase) {
    return phase === MenstrualPhase.NONE
      ? `${MenstrualPhase.NONE} (children's ranges)`
      : phase;
  }

  menstrualPhase() {
    const { section, bloodwork, previousBloodwork } = this.props;
    if (section !== Section.SEX_HORMONE) {
      return null;
    }
    const menstrualPhase = bloodwork.menstrualPhase;
    if (!menstrualPhase) {
      return null;
    }
    const previousMenstrualPhase = previousBloodwork && previousBloodwork.menstrualPhase;
    return (
      // Same width guard as the notes below: this line is longer on one line
      // than the bar-graph table, and the panel's card-body sizes itself to its
      // widest content.
      <div className="ref-range-menstrual-phase">
        <span style={{ marginLeft: '20px', fontWeight: 'bold' }}>
          Menstrual Phase
        </span>
        <span style={{ marginLeft: '8px' }}>
          {this.formatMenstrualPhase(menstrualPhase)}
        </span>
        {previousMenstrualPhase && (
          <>
            <span style={{ marginLeft: '24px', fontWeight: 'bold' }}>
              Menstrual Phase for Previous Collection Date:
            </span>
            <span style={{ marginLeft: '8px' }}>
              {this.formatMenstrualPhase(previousMenstrualPhase)}
            </span>
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip id="previous-menstrual-phase-tooltip">
                  <div>
                    The previous blood drop is plotted at its position within the
                    {' '}{this.formatMenstrualPhase(previousMenstrualPhase)} reference range.
                  </div>
                  <div style={{ marginTop: '10px' }}>
                    Even though this chart shows the
                    {' '}{this.formatMenstrualPhase(menstrualPhase)} bands, judge the
                    previous value only by the
                    {' '}{this.formatMenstrualPhase(previousMenstrualPhase)} range.
                  </div>
                  <div style={{ marginTop: '10px' }}>
                    For example, a drop at the low end of optimal is at the low end
                    of optimal for {this.formatMenstrualPhase(previousMenstrualPhase)},
                    not {this.formatMenstrualPhase(menstrualPhase)}.
                  </div>
                </Tooltip>
              }
            >
              <FontAwesomeIcon
                icon="info-circle"
                style={{ marginLeft: '8px', color: 'var(--primary)', cursor: 'pointer' }}
              />
            </OverlayTrigger>
          </>
        )}
      </div>
    );
  }

  /**
   * The rule above or below the block of text results.
   *
   * A row of its own rather than a border on the first and last text rows,
   * because the space this puts between the last chart's axis numbers and the
   * line has to sit ABOVE the line — and padding on a cell is inside its own
   * border, which would put the space under it.
   */
  textRule(edge: 'top' | 'bottom') {
    return (
      <tr key={`text-rule-${edge}`}>
        <td
          colSpan={2}
          className={`ref-range-text-rule ref-range-text-rule-${edge}`}
        />
      </tr>
    );
  }

  /**
   * One practitioner-typed marker whose result is words.
   *
   * Rendered into the SAME table as the bar graphs rather than a table of its
   * own, so the marker names line up down one column: a urinalysis sitting
   * under a chemistry panel in a second table with its own column widths reads
   * as a different document stapled to the bottom of this one.
   *
   * Across the cell where a bar would be, the result is laid out in labeled
   * columns — Result, then Expected Result when the panel has any expectation
   * to print. The columns are fixed widths rather than content-sized, so they
   * line up down the panel the same way the names do; a row with no expectation
   * of its own simply leaves the last column empty rather than closing the gap
   * and knocking every row below it out of alignment.
   *
   * `withDivider` draws the hairline between one row and the next. Every row
   * but the first carries it: the first row's top edge is already the rule
   * that closes the bar graphs off, and a second line 18px under it would read
   * as a border around nothing.
   *
   * `nameColumnPx` is how wide the names are, measured from the names this
   * panel actually carries (see render). It reaches the stylesheet as a custom
   * property and the PDF's hyphenation as an argument — one number, or the
   * export breaks words the column has room for.
   */
  textRow(
    textMarker: CustomTextMarker,
    key: string,
    showAsterisk: boolean,
    showExpected: boolean,
    withDivider: boolean,
    nameColumnPx: number,
  ) {
    const previousOnly = textMarker.isPreviousOnly();
    const differs = textMarker.differsFromExpected();
    const previousCollectionDate = formatCollectionDate(
      this.props.previousBloodwork?.collectionDate,
    );
    /*
     * Printed under EVERY result that has one, not only the ones that moved.
     *
     * It was the changed ones only, on the grounds that repeating the same word
     * down a fourteen-line panel is noise. That reads the wrong way round on a
     * comparison: a practitioner scanning the panel cannot tell a marker whose
     * previous result was identical from one the previous collection never
     * measured, and both print as a bare result. Showing it always makes the
     * column say what it is: this row, on both collections.
     */
    const previousText = (textMarker.previousText || '').trim();
    const changed = !previousOnly && !!previousText;
    const expected = (textMarker.expected || '').trim();
    const divider = withDivider ? ' ref-range-text-row-divider' : '';
    /* The row's columns, name first.
       Inline rather than in the stylesheet because one of the five widths is
       measured per panel, and splitting a grid template across two files is how
       the halves come apart. The two LABEL columns are fixed so they line up
       down the panel; the two VALUE columns share what is left in equal parts,
       `minmax(0, 1fr)` rather than `1fr` so a long result wraps inside its
       column instead of widening it and pushing the row past the panel. */
    const grid = {
      gridTemplateColumns: showExpected
        ? `${nameColumnPx}px 58px minmax(0, 1fr) 104px minmax(0, 1fr)`
        : `${nameColumnPx}px 58px minmax(0, 1fr)`,
    };
    return (
      <tr key={key}>
        {/* ONE cell spanning both columns, so a text row is laid out by its own
            grid instead of by the table's columns. That is what lets the names
            here be wider than the chart labels' column without the charts
            giving up the width: a row that spans every column of the table
            takes part in sizing none of them. The names still line up with each
            other, because every text row's grid is the same template. */}
        <td colSpan={2} className={`ref-range-text-result-cell${divider}`}>
          <div
            className={
              showExpected
                ? 'ref-range-text-grid'
                : 'ref-range-text-grid ref-range-text-grid-result-only'
            }
            style={grid}
          >
            <span className="ref-range-text-name">
              {showAsterisk && previousOnly && (
                <span
                  className="ref-range-previous-only-asterisk"
                  title="Previous test only — see the note below the chart."
                >
                  *
                </span>
              )}
              {/* See the chart labels below: the hyphen is ours in the export,
                  the browser's on screen. */}
              {this.props.forPdf
                ? hyphenateLabel(textMarker.label, nameColumnPx)
                : textMarker.label}
            </span>
            <span className="ref-range-text-column-label">Result</span>
            <span className="ref-range-text-column-value">
              <span
                className={
                  differs
                    ? 'ref-range-text-result ref-range-text-result-differs'
                    : 'ref-range-text-result'
                }
              >
                {textMarker.displayText()}
              </span>
              {changed ? (
                <span className="ref-range-text-previous">
                  previous
                  {previousCollectionDate ? ` (${previousCollectionDate})` : ''}:{' '}
                  {previousText}
                </span>
              ) : null}
            </span>
            {/* Stated rather than implied by the color alone: the color is not
                available to a reader printing in grayscale, and a named column
                says what a bare second word next to the result never did. */}
            {showExpected ? (
              <>
                {/* Both spans render even with nothing to say, because they ARE
                    the last two grid columns — drop one and the row below
                    reflows into its place. The LABEL goes when the value does:
                    it names a column, and naming an empty cell says nothing. */}
                <span className="ref-range-text-column-label">
                  {expected ? 'Expected Result' : ''}
                </span>
                <span className="ref-range-text-column-value ref-range-text-expected">
                  {expected}
                </span>
              </>
            ) : null}
          </div>
        </td>
      </tr>
    );
  }

  render() {
    const { panelMarkers } = this.props;
    const textMarkers = this.props.textMarkers || [];
    // A section can be made ENTIRELY of text markers — a urinalysis panel is
    // exactly that — so an empty chart list is no longer an empty section.
    const hasCharts = !!panelMarkers && panelMarkers.length > 0;
    if (!hasCharts && !textMarkers.length) {
      return null;
    }

    // This is a hack because the blood drop on the first blood drop is always misaligned.
    // We take the first marker and place it at the beginning of the array and then
    // we don't display it.
    if (hasCharts) {
      const fakePanelMarker: PanelMarker = panelMarkers[0];
      panelMarkers.unshift(fakePanelMarker);
    }

    // Whether any row is a "previous-only" marker — drives the footnote below
    // the chart that explains the asterisk next to those marker names.
    //
    // When EVERY row is previous-only the section has no results in this
    // collection at all, and a per-row mark distinguishes nothing. That case
    // gets one line above the table instead, naming the collection the bars
    // come from, and the asterisks and their footnote drop out.
    const allPreviousOnly =
      panelMarkers.every(pm => this.isPreviousOnly(pm)) &&
      textMarkers.every(textMarker => textMarker.isPreviousOnly());
    const hasPreviousOnly =
      !allPreviousOnly &&
      (panelMarkers.some(pm => this.isPreviousOnly(pm)) ||
        textMarkers.some(textMarker => textMarker.isPreviousOnly()));

    // The Expected Result columns are a property of the PANEL, not of a row:
    // dropped when nothing in the panel states an expectation (the common case
    // — most results are just words), kept for every row once one does, so a
    // row with no expectation of its own leaves an empty column instead of
    // reflowing the ones above and below it.
    const hasExpected = textMarkers.some(
      textMarker => !!(textMarker.expected || '').trim(),
    );

    /*
     * How wide the marker names are, measured from the names themselves.
     *
     * The old 110px cap belonged to the BAR GRAPHS: the names shared a table
     * column with the chart labels, so a long one widening that column took its
     * width straight off the charts beside it. Text rows span both columns now
     * (see textRow) and size nothing, so the names can have the room they need
     * in any panel — a stool panel's are 193px and were printing on three lines
     * with 150px of white space past the result they named.
     *
     * A panel of short names keeps 110px: the names are right-aligned, so width
     * nothing needs would open a gap down the LEFT of the card instead.
     */
    const nameColumnPx = textLabelColumnWidth(
      textMarkers.map(textMarker => textMarker.label),
    );

    /*
     * ...and the bar graphs keep the column they have today.
     *
     * The label column used to be floored at 110px by the text rows themselves
     * (`.ref-range-category-wrap`, min-width). Now that they span the table and
     * size nothing, a panel whose chart labels are all short would have seen
     * that column collapse to the labels alone — measured: 110px to 38px in a
     * panel charting pH, handing the bar graphs 72px they never had. The floor
     * moves onto the chart rows, where the text rows had been supplying it, so
     * the charts are the width they were before any of this.
     */
    const chartNameFloor = textMarkers.length
      ? { minWidth: LABEL_COLUMN_PX }
      : undefined;

    // Both the line and the footnote name the collection those bars came from.
    // Undated, either one reads as a claim about the test the report is headed
    // with, which is the one collection it is definitely not about.
    const previousCollectionDate = formatCollectionDate(
      this.props.previousBloodwork?.collectionDate,
    );

    const myRangeMarkers = this.myRangeMarkers(panelMarkers);

    return (
      <div>
        {this.menstrualPhase()}
        {allPreviousOnly && (
          <div className="ref-range-previous-collection-note">
            Every marker below is from the previous collection
            {previousCollectionDate ? ` (${previousCollectionDate})` : ''}.
            This collection has no result for any of them
            {hasCharts
              ? ', so each chart shows just the previous (blue) blood drop.'
              : '.'}
          </div>
        )}
        <Table className="labsmartz-chart-table">
          <tbody>
            {(hasCharts ? panelMarkers : []).map((pm: PanelMarker, index: number) => {
              return (
                <tr key={index} style={{ display: this.showChart(index) }}>
                  <td
                    className={`ref-range-category ${pm.field.wrapChartLabel ? 'ref-range-category-wrap' : 'text-nowrap'}`}
                    style={chartNameFloor}
                  >
                    {!allPreviousOnly && this.isPreviousOnly(pm) && (
                      <span
                        className="ref-range-previous-only-asterisk"
                        title="Previous test only — see the note below the chart."
                      >
                        *
                      </span>
                    )}
                    {/* In FRONT of the name, where this panel's other row mark
                        already sits and where the footnote below repeats it.
                        Between the name and the link icon it read as a
                        separator between two things rather than as a mark on
                        the row — the label column is right-aligned, so a
                        leading dot hangs in the margin and a trailing one does
                        not. */}
                    {this.props.forPdf ? null : (
                      <MyRangeDot panelMarker={pm} />
                    )}
                    {this.props.forPdf ? (
                      /* Hyphenated HERE rather than by the stylesheet: the
                         column asks for `hyphens: auto` and html2canvas does
                         not read that property, so a name wider than the
                         column broke bare in the export while the screen above
                         showed it hyphenated. Only for the PDF — on screen the
                         browser does it properly, and only when the line really
                         breaks.

                         And only for a label that WRAPS. The other cells are
                         `text-nowrap`: their column widens to hold the name on
                         one line, so nothing there is ever too wide and a
                         hyphen put in would print mid-line, in a name that then
                         never breaks at it. */
                      pm.field.wrapChartLabel
                        ? hyphenateLabel(pm.field.getChartLabel())
                        : pm.field.getChartLabel()
                    ) : (
                      /* The link icon travels with the LAST word of the name.
                         The two nbsp alone did not keep them together: a
                         wrapping label ("Reticulocyte Production Index") put
                         "Production Index" on line two and the browser broke
                         before the icon anyway, leaving it alone on a third
                         line. Wrapping word and icon in nowrap makes the
                         break fall before the word instead: "Production" /
                         "Index [icon]". */
                      <>
                        {this.chartLabelHead(pm)}
                        <span className="text-nowrap">
                          {this.chartLabelTail(pm)}
                          &nbsp;&nbsp;
                          {pm.field.labCorpLink ? (
                            <OverlayTrigger overlay={this.labCorpInfoTooltip(pm)}>
                              <FontAwesomeIcon
                                icon="external-link-alt"
                                onClick={() => {
                                  this.openWindow(pm);
                                }}
                              />
                            </OverlayTrigger>
                          ) : null}
                        </span>
                      </>
                    )}
                    <br />
                    <span className="ref-range-category-value">
                      {this.isPreviousOnly(pm)
                        ? pm.previousPanelMarker!.displayValue()
                        : pm.displayValue()}{' '}
                      {pm.displayUnits(true)}
                    </span>
                  </td>
                  <td>{this.displayChart(pm, index)}</td>
                </tr>
              );
            })}
            {/* Under the bar graphs, in entry order. A marker with no number
                cannot be sorted among ones that have them, and grouping them
                at the foot of the panel is also how a lab report prints a
                urinalysis: the measurements, then the observations. */}
            {textMarkers.length > 0 && this.textRule('top')}
            {textMarkers.map((textMarker, index) =>
              this.textRow(
                textMarker,
                `text-${index}`,
                !allPreviousOnly,
                hasExpected,
                index > 0,
                nameColumnPx,
              ),
            )}
            {textMarkers.length > 0 && this.textRule('bottom')}
          </tbody>
        </Table>
        {/* One line in the panel, which is what a footnote should be. "only"
            already carries "this collection has no value for it", so spelling
            that out cost a second line and said nothing new. Budget for edits:
            about 110 characters including the date, measured at this panel's
            fixed width — past that it wraps. */}
        {hasPreviousOnly && (
          <div className="ref-range-previous-only-note">
            <span className="ref-range-previous-only-asterisk">*</span>
            {' '}Result from the previous collection
            {previousCollectionDate ? ` (${previousCollectionDate})` : ''} only
            {hasCharts ? ', so the chart shows just its blue blood drop.' : '.'}
          </div>
        )}
        {/* Which markers in THIS panel are on the practitioner's own ranges.
            Named rather than left to the dots, because a practitioner
            reviewing a report before an appointment should not have to scan
            six bars for a mark. Screen only, like the dots. */}
        {myRangeMarkers.length > 0 && (
          <div className="ref-range-my-range-note">
            <span className="ref-range-my-range-dot">&bull;</span>{' '}
            {myRangeNote(myRangeMarkers.map(pm => pm.field.getChartLabel()))}
          </div>
        )}
      </div>
    );
  }
}
